import { faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Flex,
  InfoCircleIcon,
  Text,
  TextButton,
  Tooltip,
  useOpenModal,
} from '@modusign/design-system';
import { textNeutralVeryLight } from '@modusign/design-system/colors';
import capitalize from 'lodash/capitalize';

import Badge from '@app/features/authorization/components/Badge';
import {
  ExternalDocumentUsageExceededModal,
  ExternalDocumentUsageModal,
} from '@app/features/externalDocument/components/ExternalDocumentUsageModal';
import useExternalDocumentLimit from '@app/features/externalDocument/hooks/useExternalDocumentLimit';
import UsageProgressBar from '@app/features/subscription/components/PlanUsage/UsageProgressBar';

const RESOURCE_NAME = {
  en: 'externalDocument',
  ko: '외부문서',
};

export default function ExternalDocumentQuantity() {
  const openModal = useOpenModal();

  const limit = useExternalDocumentLimit();

  return (
    <Flex alignItems="center" px={[4, null, 0]}>
      <Box
        display="grid"
        height="40px"
        sx={{
          gridTemplateAreas: [
            `
            'remaining${capitalize(RESOURCE_NAME.en)}Count progressBar ${
              RESOURCE_NAME.en
            }Limit infoIcon'
            `,
            null,
            `
            'remaining${capitalize(RESOURCE_NAME.en)}Count infoIcon'
            'progressBar ${RESOURCE_NAME.en}Limit'
            `,
          ],
          gridColumn: '1 span',
        }}
      >
        <TextButton
          fontSize="sm"
          mr={4}
          sx={{
            display: 'flex',
            gap: '2px',
            gridArea: `remaining${capitalize(RESOURCE_NAME.en)}Count`,
            textDecoration: 'none',
            color: 'textNeutralLight',
            alignItems: 'center',
            '&:hover': {
              '.usageCount': {
                textDecoration: 'underline',
                textUnderlineOffset: '0.2em',
              },
            },
          }}
          onClick={() => {
            if (limit.type === 'EXCEED_LIMIT') {
              openModal((modalProps) => (
                <ExternalDocumentUsageExceededModal
                  {...modalProps}
                  limit={limit.limit}
                  usage={limit.usage}
                />
              ));
            } else {
              openModal((modalProps) => (
                <ExternalDocumentUsageModal
                  {...modalProps}
                  limit={limit.limit}
                  usage={limit.usage}
                />
              ));
            }
          }}
        >
          <Text>잔여량 : </Text>
          {limit.type === 'MEET_LIMIT' || limit.type === 'EXCEED_LIMIT' ? (
            <>
              <Text color="textNegativeBase">없음</Text>
              {limit.type === 'EXCEED_LIMIT' && (
                <Badge size="small" sx={{ marginTop: '2px' }} variant="danger">
                  초과 사용
                </Badge>
              )}
            </>
          ) : (
            <Text className="usageCount">{(limit.limit - limit.usage).toLocaleString()}개</Text>
          )}
          <FontAwesomeIcon
            color={textNeutralVeryLight}
            icon={faChevronRight}
            size="sm"
            style={{ marginLeft: '4px' }}
          />
        </TextButton>
        <Flex alignItems="center" sx={{ gridArea: 'infoIcon' }}>
          <Tooltip
            content={
              <Text fontSize="sm">
                잔여량이란? <br />
                기본 제공량에서 워크스페이스에 등록한
                <br />
                전체 {RESOURCE_NAME.ko} 수를 차감한 잔여 사용량입니다.
              </Text>
            }
            placement="bottom-end"
            scale="md"
            zIndex={1}
          >
            <InfoCircleIcon color="iconNeutralBase" height="18px" mb="3px" width="18px" />
          </Tooltip>
        </Flex>
        <Flex alignItems="center" mr={3} sx={{ gridArea: 'progressBar' }} width="150px">
          <UsageProgressBar limit={limit.limit} usage={limit.usage} />
        </Flex>
        <Flex alignItems="center" sx={{ gridArea: `${RESOURCE_NAME.en}Limit` }}>
          <Text color="textNeutralLight" fontSize="xs">
            {limit.limit.toLocaleString()}개
          </Text>
        </Flex>
      </Box>
    </Flex>
  );
}
